import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { Studio } from 'Core/Studio'
import { LiveFeedbackComponent } from 'Plugins/LiveFeedback/component/LiveFeedback'
import { LiveFeedbackIcon } from 'Components/common/Icons/LiveFeedbackIcon'
import { getLiveFeedbackWithAIApi, LiveFeedbackResponseType } from 'HTTPClients/plugins.client'
import { UpdatableReactElement, createReactElement } from 'shared/utils/react.utils'
import { sendAnalyticsTrack } from 'Utils/analytics.utils.ts'
import { Result } from 'neverthrow'
import { AxiosError } from 'axios'
import {
    DisabledStateType,
    LiveFeedbackDetailsType,
    LiveFeedbackPluginConfigType,
} from 'Types/LiveFeedback.types'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { GenerationTask } from 'Tasks/GenerationTask/GenerationTask'
import { ViolationSource, ViolationType } from 'shared/violations/types'
export class LiveFeedbackPlugin extends BasePlugin<PluginConfigType<LiveFeedbackPluginConfigType>> {
    /*
        Currently it will only be able to parse a table with a single image.
        It was done for NVIDIA VQA project to improve the quality.
    */
    public readonly id = 'Live Feedback'
    private uiElement?: UpdatableReactElement
    private liveFeedbackDetails?: LiveFeedbackDetailsType[]
    private checksMade: number = 0
    private responseFeedback: {
        parameterFeedback: Record<string, boolean>
        overallFeedback: string
    }
    private violationHighlighted: boolean = false
    private isFeedbackSuccessful: boolean = false

    constructor(
        details: PluginConfigType<LiveFeedbackPluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(details, pluginConnectors)
        this.responseFeedback = {
            parameterFeedback: {} as Record<string, boolean>,
            overallFeedback: '',
        }
    }

    public async initialize(studio: Studio) {
        this.studio = studio
        studio.workbench?.enqueueUiUpdate(accessor => {
            this.uiElement = createReactElement(LiveFeedbackComponent, {
                onLiveFeedback: async () => {
                    this.liveFeedbackDetails = await this.getFeedbackWithAI()
                    if (this.checksMade > 0) {
                        this.removeMandatoryViolation()
                    }
                    if (this.checksMade > this.config.config.limit) {
                        this.removeMandatoryResponseFeedbackViolation()
                    }
                    this.uiElement?.updater.forceUpdate()
                },
                getLiveFeedbackDetails: this.getLiveFeedbackResponse,
                getDisabledState: this.getDisabledState,
                setParameterFeedback: this.setParameterFeedback.bind(this),
                setOverallFeedback: this.setOverallFeedback.bind(this),
                getOverallFeedback: this.getOverallFeedback.bind(this),
                getParameterFeedback: this.getParameterFeedback.bind(this),
                isViolationHighlighted: this.isViolationHighlighted.bind(this),
            })
            accessor.rightSidebar.addItem({
                element: this.uiElement.element,
                title: 'Live Feedback',
                icon: LiveFeedbackIcon,
                id: this.id,
            })
        })

        this.pluginConnectors.forEach(connector => {
            const data = connector.getData()
            if (data['text']) {
                data['text'].subscribe(() => {
                    this.uiElement?.updater.forceUpdate()
                })
            }
        })
        this.checksMade = this.config.usageStats ?? 0
        this.addMandatoryViolation()
        if (this.config.config.is_feedback_required && this.checksMade < this.config.config.limit) {
            this.addMandatoryResponseFeedbackViolation()
        }
    }

    public getConfig(): PluginConfigType<LiveFeedbackPluginConfigType> {
        return this.config
    }

    public reset() {
        this.liveFeedbackDetails = undefined
        this.uiElement?.updater.forceUpdate()
    }

    private getDisabledState = (): DisabledStateType => {
        if (this.hasExceededLimit()) {
            return {
                isDisabled: true,
                reason: 'You have reached the maximum limit of getting live feedback',
            }
        }
        // const data = this.getParametersData()
        // if (!data['text']?.value) {
        //     return { isDisabled: true, reason: 'The response is empty' }
        // }
        return {
            isDisabled: false,
            reason: '',
        }
    }

    private getLiveFeedbackResponse = (): LiveFeedbackDetailsType[] | undefined => {
        return this.liveFeedbackDetails
    }

    private getAnalyticsObject = () => {
        const assignmentObject = this.studio!.taskConfig.assignment
        return {
            project_id: assignmentObject.project_id,
            task_id: assignmentObject.task.id,
            user_id: assignmentObject.user_id,
            step_id: assignmentObject.step?.id,
        }
    }

    private hasExceededLimit = (): boolean => {
        return this.checksMade >= this.config.config.limit
    }

    private handleLimitExceeded = () => {
        this.studio?.workbench.enqueueUiUpdate(ui => {
            ui.notifier.notification.error({
                message: 'Limit exceeded',
                description: 'You have reached the maximum limit of getting live feedback',
            })
        })
        this.checksMade = this.config.config.limit
    }

    private handleFeedbackFailure = (
        data: { error: { code: string; message: string } | undefined } | unknown
    ) => {
        this.isFeedbackSuccessful = false
        this.removeMandatoryResponseFeedbackViolation()
        this.studio?.workbench.enqueueUiUpdate(ui => {
            ui.notifier.notification.error({
                message:
                    (data as { error: { code: string; message: string } })?.error?.message ??
                    'Failed to get live feedback',
            })
        })
        this.checksMade += 1
    }

    private isPluginUsageLimitExceeded = (
        response: Result<LiveFeedbackResponseType, AxiosError<unknown, unknown>>
    ) => {
        return (
            response.isErr() &&
            (response.error.response?.data as { error: { message: string } })?.error?.message ===
                'plugin usage limit exhausted.'
        )
    }

    private getFeedbackWithAI = async (): Promise<LiveFeedbackDetailsType[] | undefined> => {
        sendAnalyticsTrack('LiveFeedback_Check_Init', { ...this.getAnalyticsObject() })
        const startTime = +new Date()
        const taskConfig = this.studio?.taskConfig

        const containerData = (
            this.studio?.taskManager.getTask() as GenerationTask
        )?.taskState.getCurrentState()?.submission.draft_output_container

        const feedbackResponse = await getLiveFeedbackWithAIApi({
            taskPluginId: this.config.id,
            userId: taskConfig?.assignment?.user_id ?? '',
            jobId: taskConfig?.assignment?.task?.job_id ?? '',
            refId: taskConfig?.assignment?.task?.id ?? '',
            inputContainer: taskConfig?.assignment?.task?.input_container,
            containerData: containerData,
        })
        if (feedbackResponse.isErr()) {
            sendAnalyticsTrack('LiveFeedback_Check_Failure', { ...this.getAnalyticsObject() })
            if (this.isPluginUsageLimitExceeded(feedbackResponse)) {
                this.handleLimitExceeded()
            } else {
                this.handleFeedbackFailure(feedbackResponse.error.response?.data)
            }
            return
        }
        const endTime = +new Date()

        const isSuccess = feedbackResponse.value.status === 'success'

        sendAnalyticsTrack('LiveFeedback_Check_Success', {
            ...this.getAnalyticsObject(),
            model_response_time: endTime - startTime,
            is_success: isSuccess,
        })
        this.checksMade++
        this.isFeedbackSuccessful = isSuccess

        const feedbackDetails: LiveFeedbackDetailsType[] = Object.entries(
            isSuccess ? feedbackResponse.value.response?.QC : {}
        ).map(([_, feedback]) => {
            return {
                title: feedback.title,
                value: feedback.reasoning,
                id: feedback.qc_param_version_id,
                prediction: feedback.prediction,
            }
        })

        this.responseFeedback.overallFeedback = ''
        this.responseFeedback.parameterFeedback = {}
        if (this.config.config.is_feedback_required) {
            this.addMandatoryResponseFeedbackViolation()
        }

        return feedbackDetails.length ? feedbackDetails : undefined
    }

    private addMandatoryViolation() {
        if ((this.config.usageStats ?? 0) < this.config.config.min_required_runs) {
            this.pluginConnectors.forEach(connector => {
                this.studio?.inputManager.addViolation({
                    id: `mandatory_live_feedback_${connector.id}`,
                    message: 'You need to run Live feedback at least once before submitting',
                    type: 'error',
                    source: ViolationSource.Plugin,
                })
            })
        }
    }

    private addMandatoryResponseFeedbackViolation() {
        if (this.config.config.is_feedback_required) {
            this.pluginConnectors.forEach(connector => {
                this.studio?.inputManager.addViolation({
                    id: `live_feedback_mandatory_response_feedback_${connector.id}`,
                    message:
                        'Please provide your feedback for each parameter in the Live Feedback plugin',
                    type: 'error',
                    source: ViolationSource.Plugin,
                })
            })
        }
    }

    private removeMandatoryResponseFeedbackViolation() {
        this.violationHighlighted = false
        if (this.config.config.is_feedback_required) {
            this.pluginConnectors.forEach(connector => {
                this.studio?.inputManager.removeViolation(
                    `live_feedback_mandatory_response_feedback_${connector.id}`,
                    'error'
                )
            })
        }
    }

    private removeMandatoryViolation() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.removeViolation(
                `mandatory_live_feedback_${connector.id}`,
                'error'
            )
        })
    }

    private setParameterFeedback = (parameterId: string, isAccepted: boolean) => {
        this.responseFeedback.parameterFeedback[parameterId] = isAccepted
        if (this.isAllParameterFeedbackProvided()) {
            this.removeMandatoryResponseFeedbackViolation()
        }
        this.uiElement?.updater.forceUpdate()
    }

    private setOverallFeedback = (overallFeedback: string) => {
        this.responseFeedback.overallFeedback = overallFeedback
        this.uiElement?.updater.forceUpdate()
    }

    private getOverallFeedback = (): string => {
        return this.responseFeedback.overallFeedback
    }

    private getParameterFeedback = (): Record<string, boolean> => {
        return this.responseFeedback.parameterFeedback
    }

    getPluginData(): Record<string, unknown> | null {
        return {
            qc_agent_data: {
                task_plugin_config_id: this.config.id,
                is_feedback_required: this.config.config.is_feedback_required,
                feedback: this.transformPluginData(),
                ...(this.config.config.min_required_runs > 0
                    ? { is_feedback_successful: this.isFeedbackSuccessful }
                    : {}),
            },
        }
    }

    private transformPluginData = (): Record<
        string,
        { parameter_version_id: string; is_accepted: boolean }[] | string
    > => {
        return {
            parameter_feedback: Object.entries(this.responseFeedback.parameterFeedback).map(
                ([id, isAccepted]) => ({ parameter_version_id: id, is_accepted: isAccepted })
            ),
            overall_comment: this.responseFeedback.overallFeedback,
        }
    }

    private isAllParameterFeedbackProvided = (): boolean => {
        console.log(
            Object.values(this.responseFeedback.parameterFeedback).length,
            this.liveFeedbackDetails?.length
        )
        return (
            Object.values(this.responseFeedback.parameterFeedback).length ===
            this.liveFeedbackDetails?.length
        )
    }

    public highlightViolation(errors: Record<string, ViolationType>): void {
        this.violationHighlighted = Object.values(errors).some(error =>
            error.id.includes('live_feedback_mandatory_response_feedback')
        )
        this.uiElement?.updater.forceUpdate()
    }

    private isViolationHighlighted = (): boolean => {
        return this.violationHighlighted
    }
}
